import { useMemo, useState } from "react";

export type AttachmentOutcomeAction =
  | "AttachmentComplete"
  | "PatientWithdrew"
  | "PatientAcceptedElsewhere"
  | "NoShow"
  | "ClinicDeclinedAfterMeetAndGreet"
  | "MovedToWaitlist";

type NoOutcomeOption = {
  value: AttachmentOutcomeAction;
  label: string;
  helper: string;
  sendsPatientEmail: boolean;
};

type PatientRequestAttachmentOutcomeCardProps = {
  currentStatus?: string | null;
  disabled?: boolean;
  submittingAction?: AttachmentOutcomeAction | null;
  onFinalize: (
    action: AttachmentOutcomeAction,
    note?: string | null
  ) => void | Promise<void>;
};

const NO_OUTCOME_OPTIONS: NoOutcomeOption[] = [
  {
    value: "PatientWithdrew",
    label: "Patient withdrew / declined",
    helper:
      "Use when the patient or applicant confirmed they do not want to proceed.",
    sendsPatientEmail: false,
  },
  {
    value: "PatientAcceptedElsewhere",
    label: "Patient accepted elsewhere",
    helper: "Use when the patient has found another family doctor or clinic.",
    sendsPatientEmail: false,
  },
  {
    value: "NoShow",
    label: "Meet & Greet was not completed",
    helper:
      "Use when the meet and greet did not happen or was not completed. The patient will be emailed a link to submit a new request if still interested.",
    sendsPatientEmail: true,
  },
  {
    value: "ClinicDeclinedAfterMeetAndGreet",
    label: "Clinic declined after Meet & Greet",
    helper:
      "Use when the clinic is not proceeding after the meet and greet stage. The patient will receive a closure email.",
    sendsPatientEmail: true,
  },
  {
    value: "MovedToWaitlist",
    label: "Move back to waitlist",
    helper:
      "Use when the request should remain active but no appointment/attachment can proceed right now. The patient will receive a waitlist email.",
    sendsPatientEmail: true,
  },
];

function normalizeStatus(status: string | null | undefined): string {
  return String(status || "")
    .trim()
    .toLowerCase();
}

export default function PatientRequestAttachmentOutcomeCard({
  currentStatus,
  disabled = false,
  submittingAction = null,
  onFinalize,
}: PatientRequestAttachmentOutcomeCardProps) {
  const [answer, setAnswer] = useState<"yes" | "no" | null>(null);
  const [selectedNoOutcome, setSelectedNoOutcome] =
    useState<AttachmentOutcomeAction | "">("");
  const [note, setNote] = useState("");

  const isAccepted = normalizeStatus(currentStatus) === "accepted";
  const isBusy = !!submittingAction;
  const isDisabled = disabled || isBusy || !isAccepted;

  const selectedOption = useMemo(
    () => NO_OUTCOME_OPTIONS.find((x) => x.value === selectedNoOutcome) || null,
    [selectedNoOutcome]
  );

  if (!isAccepted) {
    return null;
  }

  async function handleYes() {
    setAnswer("yes");
    await onFinalize("AttachmentComplete", note.trim() || null);
  }

  async function handleSaveNoOutcome() {
    if (!selectedNoOutcome) return;
    await onFinalize(selectedNoOutcome, note.trim() || null);
  }

  const actionButtonStyle: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: 56,
    width: "100%",
    minWidth: 0,
    padding: "12px 14px",
    fontWeight: 900,
    lineHeight: 1.2,
    textAlign: "center",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    wordBreak: "normal",
    borderRadius: 14,
  };

  return (
    <section className="ocf-section ocf-attachment-outcome-card">
      <div style={{ marginTop: 0 }}>
        <div
          className="ocf-attachment-outcome-title"
          style={{
            fontWeight: 900,
            color: "#ffffff",
            fontSize: 15,
            marginBottom: 10,
          }}
        >
          Was attachment completed?
        </div>

        <div
          className="ocf-attachment-outcome-actions"
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 220px), 1fr))",
            gap: 10,
            width: "100%",
            minWidth: 0,
          }}
        >
          <button
            type="button"
            className="ocf-btn ocf-btn--good ocf-attachment-outcome-btn ocf-attachment-outcome-btn--yes"
            disabled={isDisabled}
            onClick={handleYes}
            style={{
              ...actionButtonStyle,
              border: "1px solid #bbf7d0",
              background: "#ffffff",
              color: "#064e3b",
              boxShadow: "0 10px 22px rgba(15, 23, 42, 0.2)",
            }}
          >
            {submittingAction === "AttachmentComplete"
              ? "Marking complete…"
              : "Yes — Attachment Completed"}
          </button>

          <button
            type="button"
            className="ocf-btn ocf-btn--danger ocf-attachment-outcome-btn ocf-attachment-outcome-btn--no"
            disabled={isBusy || disabled}
            onClick={() => setAnswer("no")}
            style={{
              ...actionButtonStyle,
              border: answer === "no" ? "1px solid #fecaca" : "1px solid #fca5a5",
              background: answer === "no" ? "#dc2626" : "#fee2e2",
              color: answer === "no" ? "#ffffff" : "#7f1d1d",
              boxShadow: answer === "no"
                ? "0 10px 22px rgba(127, 29, 29, 0.32)"
                : "0 10px 22px rgba(15, 23, 42, 0.18)",
            }}
          >
            No — Not Completed
          </button>
        </div>

        <div
          className="ocf-muted ocf-attachment-outcome-helper"
          style={{
            marginTop: 8,
            lineHeight: 1.45,
            color: "rgba(248, 250, 252, 0.92)",
          }}
        >
          Choose “Yes” only when the patient has joined the practice and care has
          begun.
        </div>
      </div>

      {answer === "no" ? (
        <div
          className="ocf-attachment-outcome-no-panel"
          style={{
            marginTop: 16,
            padding: 14,
            borderRadius: 14,
            border: "1px solid #e6edf7",
            background: "#ffffff",
            minWidth: 0,
          }}
        >
          <label
            style={{
              display: "block",
              fontWeight: 900,
              color: "#102a43",
              marginBottom: 8,
            }}
          >
            Why was attachment not completed?
          </label>

          <select
            className="ocf-select"
            value={selectedNoOutcome}
            onChange={(e) =>
              setSelectedNoOutcome(e.target.value as AttachmentOutcomeAction)
            }
            disabled={isBusy || disabled}
            style={{ width: "100%" }}
          >
            <option value="">Select outcome…</option>
            {NO_OUTCOME_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          {selectedOption ? (
            <div
              style={{
                marginTop: 10,
                padding: "10px 12px",
                borderRadius: 12,
                border: selectedOption.sendsPatientEmail
                  ? "1px solid #f5df9d"
                  : "1px solid #dbe7f3",
                background: selectedOption.sendsPatientEmail
                  ? "#fffbeb"
                  : "#f8fbff",
                color: "#334e68",
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              <strong>{selectedOption.label}</strong>
              <div style={{ marginTop: 4 }}>{selectedOption.helper}</div>
              {selectedOption.sendsPatientEmail ? (
                <div style={{ marginTop: 6, fontWeight: 800 }}>
                  This outcome sends an updated email to the patient.
                </div>
              ) : (
                <div style={{ marginTop: 6, fontWeight: 800 }}>
                  This outcome does not send a patient email.
                </div>
              )}
            </div>
          ) : null}

          <label
            style={{
              display: "block",
              fontWeight: 900,
              color: "#102a43",
              marginTop: 14,
              marginBottom: 8,
            }}
          >
            Optional internal note
          </label>

          <textarea
            className="ocf-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            disabled={isBusy || disabled}
            placeholder="Optional note, e.g. patient called to cancel, appointment not attended, clinic moved request to waitlist…"
            style={{ resize: "vertical" }}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginTop: 12,
            }}
          >
            <button
              type="button"
              className="ocf-btn ocf-btn--primary"
              disabled={!selectedNoOutcome || isBusy || disabled}
              onClick={handleSaveNoOutcome}
              style={{
                minHeight: 44,
                fontWeight: 900,
                whiteSpace: "normal",
                textAlign: "center",
              }}
            >
              {submittingAction && submittingAction !== "AttachmentComplete"
                ? "Saving outcome…"
                : "Save Outcome"}
            </button>
          </div>
        </div>
      ) : null}
    </section>
  );
}